import type { Board, Coord, Effect } from '../core/types.js';
import { coordKey } from '../core/types.js';
import { buildBoardMeshes, cellPosition, type BoardMeshes } from './cells.js';
import {
  TweenSet,
  createFlipLoopTween,
  createFlipTween,
  createWobbleTween,
  type CancellableTween,
} from './animation.js';
import { launchConfetti, type ConfettiSystem } from './effects.js';
import { fitCameraToBoard, handleResize, createSceneBundle, type SceneBundle } from './scene.js';
import { pickCell } from './raycaster.js';
import { runBoardTransform, runColorSweep } from './boardfx.js';
import { materialFor } from './materials.js';
import { flashScreen, popSolvedBanner, runBoardPulse, runBoardShake } from './celebration.js';

export interface GameRenderer {
  setBoard(board: Board): void;
  applyEffects(effects: readonly Effect[], board: Board): void;
  pickAt(ev: { clientX: number; clientY: number }): Coord | null;
  destroy(): void;
}

export const createRenderer = (parent: HTMLElement): GameRenderer => {
  const bundle: SceneBundle = createSceneBundle(parent);
  let meshes: BoardMeshes | null = null;
  const tweens = new TweenSet();
  const confettis: ConfettiSystem[] = [];
  const flipLoops = new Map<string, CancellableTween>();
  let last = performance.now();
  let currentBoard: Board | null = null;

  const tick = (now: number): void => {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    tweens.step(dt);
    for (let i = confettis.length - 1; i >= 0; i--) confettis[i]!.step(dt);
    void meshes;
    bundle.controls.update();
    bundle.renderer.render(bundle.scene, bundle.camera);
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  const onResize = (): void => {
    handleResize(parent, bundle);
    if (currentBoard) fitCameraToBoard(bundle, currentBoard.size, 1.17);
  };
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize, { passive: true });
  onResize();

  const startFlipLoops = (board: Board, m: BoardMeshes): void => {
    flipLoops.forEach((l) => l.cancel());
    flipLoops.clear();
    for (let y = 0; y < board.size; y++) {
      for (let x = 0; x < board.size; x++) {
        const cell = board.cells[y]![x]!;
        if (cell.tile !== 'flipping' || !cell.altColor) continue;
        const matA = materialFor(cell.color, 'flipping');
        const matB = materialFor(cell.altColor, 'flipping');
        const offset = ((x * 7 + y * 13) % 25) / 10;
        const loop = createFlipLoopTween(m.cells[y]![x]!, matA, matB, 2.6, offset);
        flipLoops.set(coordKey({ x, y }), loop);
        tweens.add(loop);
      }
    }
  };

  const setBoard = (board: Board): void => {
    currentBoard = board;
    if (meshes) meshes.destroy();
    meshes = buildBoardMeshes(board, bundle.scene);
    handleResize(parent, bundle);
    fitCameraToBoard(bundle, board.size, 1.17);
    startFlipLoops(board, meshes);
  };

  const stopFlipLoopAt = (c: Coord): void => {
    const k = coordKey(c);
    const loop = flipLoops.get(k);
    if (loop) {
      loop.cancel();
      flipLoops.delete(k);
    }
  };

  const applyEffects = (effects: readonly Effect[], board: Board): void => {
    currentBoard = board;
    if (!meshes) return;
    for (const eff of effects) {
      if (eff.type === 'flood') eff.cells.forEach((c) => stopFlipLoopAt(c));
      applyEffect(eff, meshes, board, tweens, confettis, bundle);
    }
  };

  const pickAt = (ev: { clientX: number; clientY: number }): Coord | null => {
    if (!meshes) return null;
    return pickCell(ev, bundle.canvas, bundle.camera, meshes);
  };

  return {
    setBoard,
    applyEffects,
    pickAt,
    destroy() {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (meshes) meshes.destroy();
      bundle.controls.dispose();
      bundle.renderer.dispose();
      void currentBoard;
    },
  };
};

const applyEffect = (
  eff: Effect,
  meshes: BoardMeshes,
  board: Board,
  tweens: TweenSet,
  confettis: ConfettiSystem[],
  bundle: SceneBundle,
): void => {
  if (eff.type === 'flood') {
    eff.cells.forEach((c, i) => {
      const mesh = meshes.cells[c.y]![c.x]!;
      const tile = board.cells[c.y]![c.x]!.tile;
      const fromMat = mesh.material as ReturnType<typeof materialFor>;
      const toMat = materialFor(eff.to, tile);
      tweens.add(createFlipTween(mesh, fromMat, toMat, i * 0.025));
    });
    return;
  }
  if (eff.type === 'penalty') {
    eff.cells.forEach((c, i) => {
      tweens.add(createWobbleTween(meshes.cells[c.y]![c.x]!, i * 0.04));
    });
    runBoardShake(meshes.group, tweens, 0.22);
    flashScreen(document.body, 'rgba(255, 80, 80, 0.18)', 250);
    return;
  }
  if (eff.type === 'rotate') {
    runBoardTransform(meshes.group, tweens, { rotateY: (eff.degrees * Math.PI) / 180 });
    return;
  }
  if (eff.type === 'mirror') {
    runBoardTransform(meshes.group, tweens, { mirror: eff.axis });
    return;
  }
  if (eff.type === 'inversion' || eff.type === 'chaos') {
    runColorSweep(meshes, board, eff.origin, tweens);
    return;
  }
  if (eff.type === 'win') {
    const ep = cellPosition(board.size, board.end);
    const sp = cellPosition(board.size, board.start);
    confettis.push(launchConfetti(bundle.scene, ep, board.palette, 220));
    setTimeout(() => {
      confettis.push(launchConfetti(bundle.scene, sp, board.palette, 180));
    }, 220);
    setTimeout(() => {
      confettis.push(launchConfetti(bundle.scene, { x: 0, y: 0 }, board.palette, 240));
    }, 440);
    runBoardPulse(meshes.group, tweens, 1.22);
    flashScreen(document.body, 'rgba(255, 255, 255, 0.85)', 400);
    popSolvedBanner(document.body, 'SOLVED!');
    return;
  }
  if (eff.type === 'lose') {
    meshes.cells.flat().forEach((m, i) => tweens.add(createWobbleTween(m, i * 0.005)));
  }
};
